import { useSyncExternalStore } from 'react';
import { keyBetween } from '@werft/domain';
import type { AreaDto, AreaRole, AreaVisibility, FolderDto, PageDto, TaskListDto, TopicDto } from '@werft/contracts';
import { t } from '../../i18n/index.js';
import { ApiError, newOpId, newUuid } from '../../platform/api.js';
import { keys, queryClient } from '../../platform/queries.js';
import { upsertArea, upsertFolder, upsertList, upsertPage, upsertTopic } from '../../platform/realtime.js';
import { storage } from '../../platform/storage.js';
import { outbox } from '../../platform/sync.js';
import { toast } from '../../platform/toast.js';

/**
 * Bereiche und Teams (Paket P14). Verwalten über den Ausgangskorb (Overlay am Snapshot); Inhalte zuordnen nutzt die
 * Overlays der jeweiligen Werkzeuge. Die Auswahl „welcher Bereich ist offen“ gilt je Projekt für alle Werkzeuge und
 * wird im Browser gemerkt.
 */

const failed = (pid: string, err: unknown) => {
  void queryClient.invalidateQueries({ queryKey: keys.snapshot(pid) });
  if (err instanceof ApiError && err.status === 404) return;
  toast(t('sync.failed', { message: err instanceof Error ? err.message : t('task.failed') }), { kind: 'error' });
};

outbox.register('area.change', { done: (e, r) => upsertArea(e.projectId, r as AreaDto), fail: (e, err) => failed(e.projectId, err) });
outbox.register('area.dissolve', { done: (e) => void queryClient.invalidateQueries({ queryKey: keys.project(e.projectId) }), fail: (e, err) => failed(e.projectId, err) });
outbox.register('area.assign', {
  done: (e, r) => {
    const kind = (e.meta as { kind?: AssignKind } | undefined)?.kind;
    if (kind === 'list') upsertList(e.projectId, r as TaskListDto);
    else if (kind === 'folder') upsertFolder(e.projectId, r as FolderDto);
    else if (kind === 'page') upsertPage(e.projectId, r as PageDto);
    else if (kind === 'topic') upsertTopic(e.projectId, r as TopicDto);
  },
  fail: (e, err) => {
    void queryClient.invalidateQueries({ queryKey: keys.project(e.projectId) });
    failed(e.projectId, err);
  },
});

/* ---------- Verwalten ---------- */

export function createArea(projectId: string, areas: AreaDto[], meId: string, name: string, parentId: string | null, color: string | null): string {
  const opId = newOpId();
  const id = newUuid();
  const siblings = areas.filter((a) => a.parentId === parentId);
  const area: AreaDto = { id, projectId, parentId, name, color, position: keyBetween(siblings.at(-1)?.position ?? null, null), visibility: 'project', members: [{ userId: meId, role: 'lead' }], version: 0 };
  void outbox
    .enqueue({ opId, kind: 'area.change', projectId, method: 'POST', url: `/api/projects/${projectId}/areas`, body: { opId, id, name, parentId, color }, overlay: { type: 'areaCreate', area } })
    .catch(() => {});
  return id;
}

export function updateArea(a: AreaDto, patch: { name?: string; color?: string | null; visibility?: AreaVisibility }) {
  const opId = newOpId();
  void outbox
    .enqueue({ opId, kind: 'area.change', projectId: a.projectId, method: 'PATCH', url: `/api/areas/${a.id}`, body: { opId, expectedVersion: a.version, patch }, overlay: { type: 'areaPatch', areaId: a.id, fields: patch } })
    .catch(() => {});
}

export function moveArea(a: AreaDto, parentId: string | null, beforeId: string | null) {
  const opId = newOpId();
  void outbox
    .enqueue({ opId, kind: 'area.change', projectId: a.projectId, method: 'POST', url: `/api/areas/${a.id}/move`, body: { opId, expectedVersion: a.version, parentId, beforeId }, overlay: { type: 'areaPatch', areaId: a.id, fields: { parentId } } })
    .catch(() => {});
}

/** Liegt der Bereich (oder einer darüber) „nur fürs Team“? (P15) */
export function teamOnly(areas: AreaDto[], id: string | null): AreaDto | null {
  for (let a = id ? areas.find((x) => x.id === id) : undefined, n = 0; a && n < 10; a = a.parentId ? areas.find((x) => x.id === a!.parentId) : undefined, n++) {
    if (a.visibility === 'team') return a;
  }
  return null;
}

export function dissolveArea(a: AreaDto) {
  const opId = newOpId();
  void outbox.enqueue({ opId, kind: 'area.dissolve', projectId: a.projectId, method: 'DELETE', url: `/api/areas/${a.id}`, body: { opId } }).catch(() => {});
  toast(t('areas.dissolved', { name: a.name }));
}

export function setAreaMember(a: AreaDto, userId: string, role: AreaRole) {
  const opId = newOpId();
  const members = a.members.some((m) => m.userId === userId) ? a.members.map((m) => (m.userId === userId ? { ...m, role } : m)) : [...a.members, { userId, role }];
  void outbox
    .enqueue({ opId, kind: 'area.change', projectId: a.projectId, method: 'PUT', url: `/api/areas/${a.id}/members/${userId}`, body: { opId, role }, overlay: { type: 'areaPatch', areaId: a.id, fields: { members } } })
    .catch(() => {});
}

export function removeAreaMember(a: AreaDto, userId: string) {
  const opId = newOpId();
  void outbox
    .enqueue({
      opId,
      kind: 'area.change',
      projectId: a.projectId,
      method: 'DELETE',
      url: `/api/areas/${a.id}/members/${userId}`,
      body: { opId },
      overlay: { type: 'areaPatch', areaId: a.id, fields: { members: a.members.filter((m) => m.userId !== userId) } },
    })
    .catch(() => {});
}

/* ---------- Inhalte zuordnen ---------- */

export type AssignKind = 'list' | 'folder' | 'page' | 'topic';
const assignUrl = { list: '/api/lists', folder: '/api/folders', page: '/api/pages', topic: '/api/topics' } as const;

export function assignArea(kind: AssignKind, projectId: string, id: string, areaId: string | null) {
  const opId = newOpId();
  const overlay =
    kind === 'list'
      ? ({ type: 'listPatch', listId: id, fields: { areaId } } as const)
      : kind === 'folder'
        ? ({ type: 'folderPatch', folderId: id, fields: { areaId } } as const)
        : kind === 'page'
          ? ({ type: 'pagePatch', pageId: id, fields: { areaId } } as const)
          : ({ type: 'talkTopicPatch', topicId: id, fields: { areaId } } as const);
  void outbox.enqueue({ opId, kind: 'area.assign', projectId, method: 'PUT', url: `${assignUrl[kind]}/${id}/area`, body: { opId, areaId }, overlay, meta: { kind } }).catch(() => {});
}

/* ---------- Gewählter Bereich (je Projekt, für alle Werkzeuge) ---------- */

/** 'alle' = ganzes Projekt, 'allgemein' = ohne Bereich, sonst Bereichs-ID */
export type AreaFilter = string;
const key = (pid: string) => `werft.area.${pid}`;
const listeners = new Set<() => void>();
const current = new Map<string, AreaFilter>();

export function getAreaFilter(pid: string): AreaFilter {
  if (!current.has(pid)) current.set(pid, storage.get<string>(key(pid)) ?? 'alle');
  return current.get(pid)!;
}
export function setAreaFilter(pid: string, v: AreaFilter) {
  current.set(pid, v);
  storage.set(key(pid), v);
  listeners.forEach((l) => l());
}
export function useAreaFilter(pid: string, areas: AreaDto[]): AreaFilter {
  const v = useSyncExternalStore(
    (fn) => {
      listeners.add(fn);
      return () => void listeners.delete(fn);
    },
    () => getAreaFilter(pid),
  );
  // Ohne Bereiche oder mit einem inzwischen aufgelösten Bereich: ganzes Projekt
  if (!areas.length) return 'alle';
  if (v !== 'alle' && v !== 'allgemein' && !areas.some((a) => a.id === v)) return 'alle';
  return v;
}

/** Bereich samt Unterbereichen */
export function subtreeOf(areas: AreaDto[], id: string): Set<string> {
  const out = new Set([id]);
  let grew = true;
  while (grew) {
    grew = false;
    for (const a of areas) if (a.parentId && out.has(a.parentId) && !out.has(a.id)) (out.add(a.id), (grew = true));
  }
  return out;
}

/** Passt ein Inhalt mit diesem `areaId` zur Auswahl? */
export function areaMatcher(areas: AreaDto[], filter: AreaFilter): (areaId: string | null) => boolean {
  if (filter === 'alle') return () => true;
  if (filter === 'allgemein') return (x) => !x;
  const set = subtreeOf(areas, filter);
  return (x) => !!x && set.has(x);
}

/** Neue Inhalte landen im gewählten Bereich */
export const newAreaId = (filter: AreaFilter): string | null => (filter === 'alle' || filter === 'allgemein' ? null : filter);

/** Darf ich diesen Bereich verwalten? (Projektleitung oder Leitung hier bzw. darüber) */
export function canManageArea(areas: AreaDto[], areaId: string | null, meId: string, isProjectLead: boolean): boolean {
  if (isProjectLead) return true;
  let cur = areaId ? areas.find((a) => a.id === areaId) : undefined;
  while (cur) {
    if (cur.members.some((m) => m.userId === meId && m.role === 'lead')) return true;
    cur = cur.parentId ? areas.find((a) => a.id === cur!.parentId) : undefined;
  }
  return false;
}

export const depthOf = (areas: AreaDto[], id: string | null): number => {
  let n = 0;
  let cur = id ? areas.find((a) => a.id === id) : undefined;
  while (cur && n < 10) (n++, (cur = cur.parentId ? areas.find((a) => a.id === cur!.parentId) : undefined));
  return n;
};

/* ---------- Auswahl „In Bereich verschieben“ (ein Fenster für alle Werkzeuge) ---------- */

export interface AssignTarget {
  kind: AssignKind;
  projectId: string;
  id: string;
  name: string;
  areaId: string | null;
}
let target: AssignTarget | null = null;
const pickListeners = new Set<() => void>();
export const areaPicker = {
  open(t: AssignTarget) {
    target = t;
    pickListeners.forEach((l) => l());
  },
  close() {
    target = null;
    pickListeners.forEach((l) => l());
  },
};
export function useAreaPicker(): AssignTarget | null {
  return useSyncExternalStore(
    (fn) => {
      pickListeners.add(fn);
      return () => void pickListeners.delete(fn);
    },
    () => target,
  );
}

/* ---------- Bereichsleiste ein-/ausklappen (Audit F15, je Gerät) ---------- */

const barKey = 'werft.areaBar.collapsed';
const barListeners = new Set<() => void>();
let barCollapsed: boolean | undefined;
export function setAreaBarCollapsed(v: boolean) {
  barCollapsed = v;
  storage.set(barKey, v);
  barListeners.forEach((l) => l());
}
export function useAreaBarCollapsed(): boolean {
  return useSyncExternalStore(
    (fn) => {
      barListeners.add(fn);
      return () => void barListeners.delete(fn);
    },
    () => (barCollapsed ??= storage.get<boolean>(barKey) ?? false),
  );
}
